import type { Practitioner } from "@curai/js-fhir-tools";
import type { WaitingRoomItem } from "./network/useWaitingRoomDashboard";

interface BucketedSortedLiveQueuePatients {
  myAmazonQueue: WaitingRoomItem[];
  myDtcQueue: WaitingRoomItem[];
  mySuperClinicalQueue: WaitingRoomItem[];
  otherCliniciansAmazonQueue: WaitingRoomItem[];
  otherCliniciansDtcQueue: WaitingRoomItem[];
  otherSuperClinicalQueue: WaitingRoomItem[];
}

export function sortLiveQueuePatients({
  patients,
  practitioner,
}: {
  patients: WaitingRoomItem[] | undefined;
  practitioner: Practitioner;
}) {
  const sortedBuckets = patients
    // Copy before sorting — `patients` may be the React Query cache array (or a
    // frozen snapshot), and `.sort()` mutates in place.
    ?.slice()
    .sort((a, b) => b.encounter.slaWeight - a.encounter.slaWeight)
    ?.reduce<BucketedSortedLiveQueuePatients>(
      (acc, curr) => {
        const { suggestedProfessionalUserId } = curr.encounter;
        const suggestedProfessionalUserIdNumber = suggestedProfessionalUserId
          ? Number(suggestedProfessionalUserId)
          : undefined;
        // CAs (not licensed clinicians) by default have all patients from
        // /waiting-room-dashboard in their queue - only Clinicians split their
        // dashboard based on encounters where they are/arent the "suggested professional"
        const isMyQueue =
          !practitioner.isLicensedClinician ||
          suggestedProfessionalUserIdNumber === Number(practitioner?.userId);

        const isAmazon = curr.patient.partnerUids?.includes("amazon");
        const isSuperClinical =
          curr.patient.partnerUids?.includes("superclinical");

        if (isMyQueue) {
          isAmazon
            ? acc.myAmazonQueue.push(curr)
            : isSuperClinical
              ? acc.mySuperClinicalQueue.push(curr)
              : acc.myDtcQueue.push(curr);
        } else {
          isAmazon
            ? acc.otherCliniciansAmazonQueue.push(curr)
            : isSuperClinical
              ? acc.otherSuperClinicalQueue.push(curr)
              : acc.otherCliniciansDtcQueue.push(curr);
        }
        return acc;
      },
      {
        myAmazonQueue: [],
        myDtcQueue: [],
        mySuperClinicalQueue: [],
        otherCliniciansAmazonQueue: [],
        otherCliniciansDtcQueue: [],
        otherSuperClinicalQueue: [],
      },
    ) || {
    myAmazonQueue: [],
    myDtcQueue: [],
    mySuperClinicalQueue: [],
    otherCliniciansAmazonQueue: [],
    otherCliniciansDtcQueue: [],
    otherSuperClinicalQueue: [],
  };

  return {
    myQueue: [
      ...sortedBuckets.mySuperClinicalQueue,
      ...sortedBuckets.myDtcQueue,
      ...sortedBuckets.myAmazonQueue,
    ],
    otherCliniciansQueue: [
      ...sortedBuckets.otherSuperClinicalQueue,
      ...sortedBuckets.otherCliniciansDtcQueue,
      ...sortedBuckets.otherCliniciansAmazonQueue,
    ],
  };
}
